import { BrowserRouter, Route, Switch, useRouteMatch } from 'react-router-dom'
import { RecoilRoot } from 'recoil'
import ErrorBoundary from './containers/ErrorBoundary'
import Locale from './containers/Locale'

import { STORE_KEY_AUTH } from 'Constants'
import {
  notifyParent,
  sessionId,
  sessionStoreGet,
  useConditions,
  useConsoleTracking,
  usePageTracking
} from 'mailup-react-helpers'
import { PageHeader } from 'mailup-web-components'
import AuthPage from 'Pages/AuthPage'
import CampaignsSendStatus from 'Pages/CampaignsSendStatus'
import EmailMessagePreview from 'Pages/EmailMessagePreview'
import EmailSendingWizard from 'Pages/EmailSendingWizard/EmailSendingWizard'
import MessageCheckupLinkCheck from 'Pages/MessageCheckupLinkCheck'
import MessageCheckupSpamCheck from 'Pages/MessageCheckupSpamCheck'
import MessageCheckupSummary from 'Pages/MessageCheckupSummary'
import MessageEdit from 'Pages/MessageEdit'
import OverviewMessage from 'Pages/OverviewMessage'
import PreviewMessage from 'Pages/PreviewMessage'
import PreviewTemplate from 'Pages/PreviewTemplate'
import QuickSend from 'Pages/QuickSend/QuickSend'
import ScheduledSendings from 'Pages/ScheduledSendings'
import SMTPPlusSendStatus from 'Pages/SMTPPlusSendStatus'
import TemplateEdit from 'Pages/TemplateEdit'
import TemplateList from 'Pages/TemplateList'
import AbTestList from './pages/AbTestList'
import AbTestManage from './pages/AbTestManage'
import AbTestUpsell from './pages/AbTestUpsell'
import AbTestWizard from './pages/AbTestWizard'
import EmailSendingQueueManage from './pages/EmailSendingQueueManage'
import HelloWorld from './pages/HelloWorld'
import MessageList from './pages/MessageList'
import NewMessage from './pages/NewMessage'
import SMTPPlusList from './pages/SMTPPlusList'

import useSessionCheck from 'Hooks/useSessionCheck'
import EmailContentDefaults from 'Pages/EmailContentDefaults/EmailContentDefaults'
import MessageCreate from 'Pages/MessageCreate'
import NoAuthPage from 'Pages/NoAuthPage'
import RcsBot from 'Pages/RcsBot/RcsBot'
import RcsList from 'Pages/RcsList/RcsList'
import RcsSending from 'Pages/RcsSending/RcsSending'
import RcsSendStatus from 'Pages/RcsSendStatus/RcsSendStatus'
import SaveRowEdit from 'Pages/SaveRowsEdit/SaveRowEdit'
import SmsQueue from 'Pages/SmsQueue'
import TextVersion from 'Pages/TextVersion/TextVersion'
import UrlSelection from 'Pages/UrlSelection/UrlSelection'

const NoMatchPage = () => {
  return (
    <>
      <PageHeader breadcrumbsItems={[]} />
      <div className='l-container' data-mu-test='no-match-page' />
    </>
  )
}

const withAuth = (Component, condition) => {
  return props => {
    const auth = sessionStoreGet(STORE_KEY_AUTH)
    const routeMatch = useRouteMatch()

    if (!auth) {
      notifyParent('redirectMainFrame', [
        `messages_app_redirector.aspx?r=${routeMatch.url}`
      ])
    }

    if (condition) {
      const [conditionIsSatisfied] = useConditions(condition)
      if (!conditionIsSatisfied) {
        return <NoAuthPage />
      }
    }
    return <Component {...props} />
  }
}

const withPageTracking = Component => props => {
  usePageTracking(props.path)
  useConsoleTracking()

  return <Component {...props} />
}

const CustomRoute = withAuth(withPageTracking(Route))

const App = () => {
  useSessionCheck()

  return (
    <RecoilRoot>
      <ErrorBoundary>
        <Locale>
          <BrowserRouter basename={sessionId}>
            <Switch>
              {/* eslint-disable prettier/prettier */}
              <Route path='/' component={HelloWorld} exact />
              <Route path='/auth' component={AuthPage} />
              <CustomRoute path='/messages/email/new' component={withAuth(NewMessage, '')} />
              <CustomRoute path='/messages/email/saved' component={withAuth(MessageList, 'uo:NL')} />
              <CustomRoute path='/messages/email/smtpplus' component={withAuth(SMTPPlusList, 'c:SmtpPlusOrFastEmailUsersPresent AND uo:SMTP+ AND co:SMTPV2ENABLED')} />
              <CustomRoute path='/messages/email/abtestlist' component={withAuth(AbTestList, 'uo:NL')} />
              <CustomRoute path='/messages/email/abtest-upsell' component={withAuth(AbTestUpsell, 'uo:NL')} />
              <CustomRoute path='/messages/email/abtest/:id' component={withAuth(AbTestWizard, 'uo:NL AND NOT c:IsOfferLight')} />
              <CustomRoute path='/messages/email/abtestmanage/:id' component={withAuth(AbTestManage, 'uo:NL AND NOT c:IsOfferLight')} />
              <CustomRoute path='/messages/email/bee/:mode(edit)/:id' component={withAuth(MessageEdit, 'uo:BEEEDITORENABLE')} exact />
              <CustomRoute path='/messages/email/bee/:mode(new)/:template(local|centralized)/:idTemplate' component={withAuth(MessageEdit, 'uo:BEEEDITORENABLE')} exact />
              <CustomRoute path='/messages/email/sendstatus/campaigns' component={withAuth(CampaignsSendStatus, 'uo:SEND')} />
              <CustomRoute path='/messages/email/queue-manage/:idSending' component={withAuth(EmailSendingQueueManage, 'uo:SEND')} />
              <CustomRoute path='/messages/email/sendstatus/smtpplus' component={withAuth(SMTPPlusSendStatus, 'c:SmtpPlusOrFastEmailUsersPresent AND uo:SMTP+ AND co:SMTPV2ENABLED')} />
              <CustomRoute path='/messages/email/sendstatus/scheduled' component={withAuth(ScheduledSendings, '')} />
              <CustomRoute path='/messages/email/:id/preview' component={withAuth(PreviewMessage, '')} exact />
              <CustomRoute path='/messages/email/:id/overview' component={withAuth(OverviewMessage, 'uo:NL')} />
              <CustomRoute path='/messages/email/:id/previews' component={withAuth(EmailMessagePreview, '')} exact />
              <CustomRoute path='/messages/email/:id/send' component={withAuth(EmailSendingWizard, 'uo:NL')} exact />
              <CustomRoute path='/messages/email/:id/quicksend' component={withAuth(QuickSend, 'uo:NL AND NOT uo:NOSEND')} exact />
              <CustomRoute path='/messages/email/contentdefaults' component={withAuth(EmailContentDefaults, '')} />

              <CustomRoute path='/messages/email/:id/checkup/summary' component={withAuth(MessageCheckupSummary, 'uo:NL')} exact />
              <CustomRoute path='/messages/email/:id/checkup/linkcheck' component={withAuth(MessageCheckupLinkCheck, 'uo:NL')} exact />
              <CustomRoute path='/messages/email/:id/checkup/spamcheck' component={withAuth(MessageCheckupSpamCheck, 'uo:NL')} exact />

              <CustomRoute path='/messages/email/import/:type' component={withAuth(MessageCreate, 'uo:NL AND NOT c:IsOfferLight')} />

              <CustomRoute path='/templates/:kind(bee)/:mode(edit|copy)/:id' component={withAuth(TemplateEdit, 'uo:BEEEDITORENABLE')} exact />
              <CustomRoute path='/templates/:kind(bee)/:source?' component={withAuth(TemplateList, 'uo:BEEEDITORENABLE')} exact />
              <CustomRoute path='/templates/:kind(bee|html)/:local(Local|Centralized)/preview/:id' component={withAuth(PreviewTemplate, '')} exact />
              <CustomRoute path='/templates/:kind(bee|html)/:local(Local|Centralized)/preview/:id/:msgtype(confirmation|welcome)' component={withAuth(PreviewTemplate, '')} exact />

              {/* 'uo:SMS AND uo:SEND' */}
              <CustomRoute path='/messages/sms/queue/:idInvio/:idSms/:idConfiguration?' component={withAuth(SmsQueue, 'uo:SMS AND uo:SEND')} />
              <CustomRoute path='/messages/email/:id/text-version' component={withAuth(TextVersion, 'uo:NL')}
              />
              <CustomRoute path='/messages/sms/url-selection' component={withAuth(UrlSelection, '')}
              />
              <CustomRoute path='/messages/email/save-row/:id' component={withAuth(SaveRowEdit, 'uo:NL')} />
              <CustomRoute path='/messages/rcs/bot' component={withAuth(RcsBot, '')}
              />
              <CustomRoute path='/messages/rcs/sending/:idBot/:idMessage' component={withAuth(RcsSending, '')}
              />
              <CustomRoute path='/messages/rcs/list' component={withAuth(RcsList, '')}
              />
              <CustomRoute path='/messages/rcs/sendstatus' component={withAuth(RcsSendStatus, '')}
              />
              <CustomRoute path='/noauth' component={NoAuthPage} exact />
              <CustomRoute component={NoMatchPage} />
              {/* eslint-enable prettier/prettier */}
            </Switch>
          </BrowserRouter>
        </Locale>
      </ErrorBoundary>
    </RecoilRoot>
  )
}

export default App
